import { useState, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { baseTheme } from "@acto/web-common/theme";
import { Card, MetaLabel } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import {
  TrendBarChart,
  type TrendBarChartProps,
} from "@acto/taskpane/field-sales-2/components/primitives/TrendBarChart";
import { useHasFinePointer } from "@acto/taskpane/field-sales-2/utils/hooks/useHasFinePointer";
import { ComparisonLegend, type ComparisonLegendRow } from "./ComparisonLegend";

const STALE_OPACITY = 0.5;

const CARD_PADDING = {
  paddingTop: baseTheme.fieldSalesTwo.spacing[32],
  paddingBottom: baseTheme.fieldSalesTwo.spacing[24],
};

const Header = styled("header")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
}));

const Title = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["18"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["130"],
  color: theme.fieldSalesTwo.colors.neutral[850],
  overflowWrap: "anywhere",
}));

const DimmingOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  transition: "opacity 150ms ease",
}));

const ChartSection = styled("div")(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[24],
}));

/** What the card shows for one span: the whole period or one focused bar. */
export interface KpiCardView {
  legend: ComparisonLegendRow[];
}

export interface KpiCardTrend<View extends KpiCardView> {
  isPending: boolean;
  /** True once the query has resolved with no current-period datapoints. */
  isEmpty: boolean;
  summary: View;
  /** Per-bar focus views, aligned with `chart.data`: bucket i belongs to bar i. */
  buckets: View[];
  chart: TrendBarChartProps;
}

interface KpiCardShellProps<View extends KpiCardView> {
  subtitle: string;
  title: string;
  trend: KpiCardTrend<View>;
  renderFigures: (view: View) => ReactNode;
}

/** Tap-to-focus is enabled for coarse pointers only. */
export function KpiCardShell<View extends KpiCardView>({
  subtitle,
  title,
  trend,
  renderFigures,
}: KpiCardShellProps<View>) {
  const [selection, setSelection] = useState<{ key: string; index: number } | null>(null);
  const isCoarsePointer = !useHasFinePointer();

  // A new card subject or range invalidates the focused index, so the selection carries the key it
  // was made under and stops matching when one changes.
  const focusKey = `${subtitle}|${title}|${trend.chart.periodStarts.join("|")}`;
  const selectedBar = isCoarsePointer && selection?.key === focusKey ? selection.index : null;

  if (trend.isEmpty) return null;

  const view = (selectedBar !== null ? trend.buckets[selectedBar] : undefined) ?? trend.summary;

  return (
    <Card style={CARD_PADDING}>
      <Header>
        <MetaLabel>{subtitle}</MetaLabel>
        <Title>{title}</Title>
      </Header>
      <Divider />
      <DimmingOverlay
        aria-busy={trend.isPending}
        style={{
          opacity: trend.isPending ? STALE_OPACITY : 1,
          pointerEvents: trend.isPending ? "none" : undefined,
        }}
      >
        {renderFigures(view)}
        <Divider />
        <ComparisonLegend rows={view.legend} />
        <ChartSection>
          <TrendBarChart
            {...trend.chart}
            selectedIndex={selectedBar}
            onSelect={
              isCoarsePointer
                ? (index) => setSelection(selectedBar === index ? null : { key: focusKey, index })
                : undefined
            }
          />
        </ChartSection>
      </DimmingOverlay>
    </Card>
  );
}
